/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { ListeTable } from '@mairistem/liste';

import { Checkbox, Dropdown, Input } from '@mairistem/composants';

import type { Csp, Profession } from '@/types';
import type { ListeTableRowProps } from '@mairistem/liste/lib/ts/src/components/ListeTable/ListeTableRow';

interface Props {
  /** Profession de la ligne */
  profession?: Profession;
  /** Props de la ligne */
  rowProps?: ListeTableRowProps<Profession>;
  /** Utilitaires de la ligne */
  utilities?: {
    onChange: (objet: object) => void;
    autoFocus?: { value: boolean };
    disableFocus?: { value: boolean };
    disableShiftTabulation?: { value: boolean };
    disableDrag?: { value: boolean };
  };
  /** Erreurs */
  errors?: { error: string }[];
  /** Csp */
  csp: Csp[];
  /** Booléen pour gérer l'affichage des professions caduques */
  isShowingCaduque?: boolean;
  /** Callback appelé lors d'un double clic sur la ligne */
  onDoubleClick?: (profession: Profession) => void;
}

const ProfessionListeTableRow = (props: Props) => {
  const {
    profession: { identifiant, identifiantCsp, libelle, code, caduque } = {},
    rowProps = {},
    utilities = null,
    errors = [],
    csp,
    isShowingCaduque = false,
    onDoubleClick = null,
  } = props;

  const handleRowChange = React.useCallback(
    (
      _e: React.ChangeEvent<HTMLInputElement>,
      data: { name: string; value: string },
    ) => {
      const { name, value } = data;
      utilities?.onChange({ [name]: value });
    },
    [utilities],
  );

  const handleDoubleClick = React.useCallback(() => {
    if (onDoubleClick) {
      onDoubleClick({
        identifiant,
        identifiantCsp,
        libelle,
        code,
        caduque,
      });
    }
  }, [onDoubleClick, identifiant, identifiantCsp, libelle, code, caduque]);

  return (
    <ListeTable.Row
      key={identifiant}
      error={_.some(errors, { itemId: identifiant })}
      {...rowProps}
    >
      <ListeTable.Cell
        name="code"
        stretched
        // @ts-expect-error
        onDoubleClick={handleDoubleClick}
      >
        <Input
          error={_.some(errors, { itemId: identifiant, field: 'code' })}
          name="code"
          value={code}
          id={`code-${identifiant}`}
          onChange={handleRowChange}
          fluid
          transparent
          readOnly={rowProps.selection}
          {...utilities?.autoFocus}
          {...utilities?.disableFocus}
          {...utilities?.disableDrag}
          {...utilities?.disableShiftTabulation}
        />
      </ListeTable.Cell>
      <ListeTable.Cell
        name="libelle"
        stretched
        // @ts-expect-error
        onDoubleClick={handleDoubleClick}
      >
        <Input
          error={_.some(errors, { itemId: identifiant, field: 'libelle' })}
          name="libelle"
          value={libelle}
          id={`libelle-${identifiant}`}
          onChange={handleRowChange}
          fluid
          transparent
          readOnly={rowProps.selection}
          {...utilities?.disableFocus}
          {...utilities?.disableDrag}
        />
      </ListeTable.Cell>
      <ListeTable.Cell
        name="csp"
        stretched
        // @ts-expect-error
        onDoubleClick={handleDoubleClick}
      >
        <Dropdown
          placeholder="Catégorie"
          fluid
          search
          selection
          disabled={rowProps.selection === 'click'}
          value={identifiantCsp}
          id={`csp-${identifiant}`}
          onChange={handleRowChange}
          name="identifiantCsp"
          options={_.map(csp, (c) => ({
            key: c.identifiant,
            value: c.identifiant,
            text: c.libelle,
          }))}
          {...utilities?.disableFocus}
          {...utilities?.disableDrag}
        />
      </ListeTable.Cell>
      {isShowingCaduque && (
        <ListeTable.Cell
          name="caduque"
          stretched
          disabled
          verticalAlign="middle"
          textAlign="center"
        >
          <Checkbox
            checked={caduque}
            input={{ readOnly: true }}
            id={`caduque-${identifiant}`}
            name="caduque"
            // disabled
            error={_.some(errors, { itemId: identifiant, field: 'caduque' })}
          />
        </ListeTable.Cell>
      )}
    </ListeTable.Row>
  );
};

export default ProfessionListeTableRow;
